import React from 'react';
import { DiscountType } from 'components/Memberships/Add/Steps/Items/Table/DiscountField';

export type { DiscountType };
export type DiscountMap = Record<string, { value: number; type: DiscountType }>;

export interface UseDiscountedItemSelectionConfig<T> {
    /** All available items to display */
    items: T[];
    /** Currently selected items (committed — catalog objects) */
    selectedItems: T[];
    /** Existing discount values keyed by String(item.id), pre-populates on open */
    existingDiscounts: DiscountMap;
    /** Callback when selection + discounts are applied */
    onSelectedItemsChange: (items: T[], discounts: DiscountMap) => void;
    /** Filter function for search */
    filterFn: (item: T, query: string) => boolean;
}

export interface UseDiscountedItemSelectionReturn<T> {
    tempSelectedItems: T[];
    setTempSelectedItems: (items: T[]) => void;
    tempDiscounts: DiscountMap;
    setTempDiscount: (itemId: string, value: number, type: DiscountType) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    isOpen: boolean;
    filteredItems: T[];
    openDrawer: () => void;
    closeDrawer: () => void;
    handleApply: () => void;
    handleCancel: () => void;
}

export const useDiscountedItemSelection = <T>({
    items,
    selectedItems,
    existingDiscounts,
    onSelectedItemsChange,
    filterFn
}: UseDiscountedItemSelectionConfig<T>): UseDiscountedItemSelectionReturn<T> => {
    const [tempSelectedItems, setTempSelectedItems] = React.useState<T[]>([]);
    const [tempDiscounts, setTempDiscountsState] = React.useState<DiscountMap>({});
    const [searchQuery, setSearchQuery] = React.useState('');
    const [isOpen, setIsOpen] = React.useState(false);

    // Refs so openDrawer/handleCancel always read latest values without stale closures
    const selectedItemsRef = React.useRef(selectedItems);
    selectedItemsRef.current = selectedItems;

    const existingDiscountsRef = React.useRef(existingDiscounts);
    existingDiscountsRef.current = existingDiscounts;

    const onSelectedItemsChangeRef = React.useRef(onSelectedItemsChange);
    onSelectedItemsChangeRef.current = onSelectedItemsChange;

    const filteredItems = React.useMemo(() => {
        if (!searchQuery.trim()) return items;
        return items.filter(item => filterFn(item, searchQuery));
    }, [items, searchQuery, filterFn]);

    const openDrawer = React.useCallback(() => {
        setTempSelectedItems(selectedItemsRef.current);
        setTempDiscountsState(existingDiscountsRef.current);
        setIsOpen(true);
    }, []);

    const closeDrawer = React.useCallback(() => {
        setSearchQuery('');
        setIsOpen(false);
    }, []);

    const handleApply = React.useCallback(() => {
        onSelectedItemsChangeRef.current(tempSelectedItems, tempDiscounts);
        closeDrawer();
    }, [tempSelectedItems, tempDiscounts, closeDrawer]);

    const handleCancel = React.useCallback(() => {
        setTempSelectedItems(selectedItemsRef.current);
        setTempDiscountsState(existingDiscountsRef.current);
        closeDrawer();
    }, [closeDrawer]);

    const setTempDiscount = React.useCallback((itemId: string, value: number, type: DiscountType) => {
        setTempDiscountsState(prev => ({ ...prev, [itemId]: { value, type } }));
    }, []);

    return {
        tempSelectedItems,
        setTempSelectedItems,
        tempDiscounts,
        setTempDiscount,
        searchQuery,
        setSearchQuery,
        isOpen,
        filteredItems,
        openDrawer,
        closeDrawer,
        handleApply,
        handleCancel
    };
};
